"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Modal = void 0;
var tslib_1 = require("tslib");
/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
var react_1 = tslib_1.__importStar(require("react"));
var react_dom_1 = require("react-dom");
var classnames_1 = tslib_1.__importDefault(require("classnames"));
var button_1 = require("../button");
var transition_1 = require("../transition");
var create_rocket_1 = require("../_util/create-rocket");
var get_overlay_root_1 = require("../_util/get-overlay-root");
var use_visible_transition_1 = require("../_util/use-visible-transition");
var call_both_1 = require("../_util/call-both");
var ModalMessage_1 = require("./ModalMessage");
var use_outside_click_1 = require("../_util/use-outside-click");
var config_context_1 = require("../_util/config-context");
var uuid_1 = require("../_util/uuid");
var z_stack_1 = require("../_util/z-stack");
var backdrop_1 = require("../backdrop");
var use_overlay_focus_1 = require("../_util/use-overlay-focus");
var key_map_1 = require("../_util/key-map");
var i18n_1 = require("../i18n");
var util_1 = require("../util");
var forward_ref_with_statics_1 = require("../_util/forward-ref-with-statics");
var event_1 = require("./event");
var modelConfigOptions = {};
// 容器们
var ModalHeader = create_rocket_1.createRocket("ModalHeader", "div.@{prefix}-dialog__header");
var ModalBody = create_rocket_1.createRocket("ModalBody", "div.@{prefix}-dialog__body");
var ModalFooter = create_rocket_1.createRocket("ModalFooter", "div.@{prefix}-dialog__footer", "div.@{prefix}-dialog__btnwrap");
/**
 * 模态对话框组件
 */
exports.Modal = forward_ref_with_statics_1.forwardRefWithStatics(function Modal(props, fRef) {
    var _a = Object.assign({}, modelConfigOptions, props), visible = _a.visible, caption = _a.caption, size = _a.size, onClose = _a.onClose, onExited = _a.onExited, disableEscape = _a.disableEscape, disableCloseIcon = _a.disableCloseIcon, maskClosable = _a.maskClosable, _b = _a.destroyOnClose, destroyOnClose = _b === void 0 ? true : _b, className = _a.className, style = _a.style, children = _a.children, popupContainer = _a.popupContainer, maskStyle = _a.maskStyle;
    var _c = config_context_1.useConfig(), classPrefix = _c.classPrefix, globalPopupContainer = _c.popupContainer;
    var t = i18n_1.useTranslation();
    var ref = react_1.useRef(null);
    var focusRef = react_1.useRef(null);
    var idRef = react_1.useRef(uuid_1.uuid());
    var firstRender = react_1.useRef(!!visible);
    var _d = use_outside_click_1.useOutsideClick(ref), listen = _d.listen, ignoreProps = _d.ignoreProps;
    listen(function () {
        if (maskClosable && visible && z_stack_1.zStack.top === idRef.current && onClose) {
            onClose();
        }
    });
    if (visible && !firstRender.current) {
        firstRender.current = true;
    }
    // 监听 ESC 键
    react_1.useEffect(function () {
        if (!visible) {
            return function () { return null; };
        }
        var handleKeydown = function (evt) {
            if (evt.key === key_map_1.KeyMap.Esc &&
                !disableEscape &&
                z_stack_1.zStack.top === idRef.current &&
                onClose) {
                onClose();
            }
        };
        window.addEventListener("keydown", handleKeydown);
        return function () { return window.removeEventListener("keydown", handleKeydown); };
    }, [visible, disableEscape, onClose]);
    react_1.useEffect(function () {
        if (firstRender.current) {
            event_1.modalEventEmitter.emit(visible ? "open" : "close");
        }
    }, [visible]);
    var _e = use_visible_transition_1.useVisibleTransition(visible), contentIn = _e.contentIn, shouldContentEnter = _e.shouldContentEnter, shouldContentRender = _e.shouldContentRender, onContentExit = _e.onContentExit;
    react_1.useEffect(function () {
        if (contentIn) {
            var id_1 = idRef.current;
            z_stack_1.zStack.push(id_1);
            return function () {
                z_stack_1.zStack.remove(id_1);
            };
        }
        return function () { };
    }, [contentIn]);
    use_overlay_focus_1.useOverlayFocus(visible, focusRef);
    if (!shouldContentRender &&
        (destroyOnClose || (!destroyOnClose && !firstRender.current))) {
        return null;
    }
    // 有标题，或者有图标，就需要渲染 header
    var hasHeader = Boolean(caption) || !disableCloseIcon;
    // 内置尺寸名称
    var sizeClassName = null;
    if (typeof size === "string" &&
        ["s", "l", "xl", "auto"].indexOf(size) > -1) {
        sizeClassName = "size-" + size;
    }
    var dialog = (react_1.default.createElement("div", { className: classPrefix + "-overlay " + classPrefix + "-dialog-parent", style: {
            display: !destroyOnClose && !contentIn ? "none" : undefined,
        } },
        react_1.default.createElement(transition_1.FadeTransition, { in: shouldContentEnter, opacity: 1 },
            react_1.default.createElement(backdrop_1.BackDrop, { style: maskStyle })),
        react_1.default.createElement("div", { tabIndex: 0, "aria-hidden": "true", style: { width: 0, height: 0, overflow: "hidden", outline: "none" }, ref: focusRef }),
        react_1.default.createElement(transition_1.SlideTransition, { unmountOnExit: destroyOnClose, in: shouldContentEnter, onExited: call_both_1.callBoth(onExited, onContentExit) },
            react_1.default.createElement("div", { className: classnames_1.default(classPrefix + "-dialog", className), style: style, onClick: function (e) { return e.stopPropagation(); }, role: "dialog" },
                react_1.default.createElement("div", tslib_1.__assign({ ref: util_1.mergeRefs(ref, fRef), className: classnames_1.default(classPrefix + "-dialog__inner", sizeClassName), style: size > 0 || /%$/.test(String(size)) ? { width: size } : null }, ignoreProps),
                    hasHeader && (react_1.default.createElement(ModalHeader, null,
                        caption && (react_1.default.createElement("h3", { className: classPrefix + "-dialog__headertitle" }, caption)),
                        !disableCloseIcon && (react_1.default.createElement(button_1.Button, { icon: "close", htmlType: "button", "aria-label": t.closeText, onClick: onClose })))),
                    children))),
        react_1.default.createElement("div", { tabIndex: 0, "aria-hidden": "true", style: { width: 0, height: 0, overflow: "hidden", outline: "none" }, onFocus: function () {
                if (focusRef.current) {
                    focusRef.current.focus();
                }
            } })));
    return react_dom_1.createPortal(dialog, get_overlay_root_1.getOverlayRoot(popupContainer || globalPopupContainer));
}, {
    /**
     * 渲染对话框的主要内容
     */
    Body: ModalBody,
    /**
     * 渲染对话框的底部内容
     */
    Footer: ModalFooter,
    /**
     * 对话框消息内容，可置于 Body:
     */
    Message: ModalMessage_1.ModalMessage,
    /**
     * 全局配置
     */
    config: function (options) {
        if (options === void 0) { options = {}; }
        Object.assign(modelConfigOptions, options);
    },
});
exports.Modal.displayName = "ModalMain";
//# sourceMappingURL=ModalMain.js.map